import { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDrawPolygon, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { useMap } from 'react-leaflet';
import { v4 as uuidv4 } from 'uuid';

import {
  activeImageOrDuplicateOfSelector,
  activeImageSelector,
  addLabelInImage,
  addSpaceInImage,
  effectiveLabelStyleSelector,
  enabledImagesSelector,
  groupedUndo,
  labelPositionsSelector,
  OverlayStyle,
  OverlayType,
  overlayLabelsSelector,
  overlaysSelector,
  removeLabelInImage,
  removeOverlayInImage,
  rotateLabelInImage,
  preferencesSelector,
  spacePositionsSelector,
  setEditedSpace,
  spacesSelector,
  updateLabel,
  updateOverlayInImage,
  useAppDispatch,
  useAppSelector,
} from '../../../../store';
import eventBus from '../../../../eventBus';
import { OverlayPoint, polygonCentroid } from '../../../../utils/overlays';
import { EditLabelStyleFields } from '../../../labels/Sidebar/Edit/EditLabelStyleFields';
import { EditLabelToolbar } from '../../../labels/Sidebar/Edit/EditLabelToolbar';
import { LabelNameEditor } from '../../../labels/Sidebar/Edit/LabelNameEditor';
import { labelHtml, labelText } from '../../../../utils/labelText';
import type { LabelStyleWithRotation } from '../../../labels/Sidebar/Edit/EditLabel';
import { resolveLabelEditorStyleState } from '../../../labels/Sidebar/Edit/editLabelStyleState';
import type { OverlayStylePreview } from '../../Map/Overlays';

import './EditOverlay.scss';

type LinkMode = 'none' | 'new' | 'existing' | 'layer';

const NUMERIC_STYLE_ATTRIBUTES: (keyof LabelStyleWithRotation)[] = [
  'fontSize', 'backgroundOpacity', 'padding', 'skewX', 'skewY', 'perspectiveX', 'perspectiveY',
];

const NO_OVERLAYS: OverlayType[] = [];

interface EditOverlayProps {
  overlay: OverlayType;
  // Other selected overlays, getting the changed style attributes and rotation
  otherOverlays?: OverlayType[];
  onPreviewChange: (preview: OverlayStylePreview | null) => void;
  // Where the label not created yet was dragged to, instead of the overlay centroid
  newLabelPosition?: OverlayPoint | null;
  onClose: () => void;
}

export function EditOverlay({
  overlay, otherOverlays = NO_OVERLAYS, onPreviewChange, newLabelPosition, onClose,
}: EditOverlayProps) {
  const dispatch = useAppDispatch();
  const map = useMap();
  const overlayLabels = useAppSelector(overlayLabelsSelector);
  const overlays = useAppSelector(overlaysSelector);
  const spaces = useAppSelector(spacesSelector);
  const activeImage = useAppSelector(activeImageSelector);
  const ownerImage = useAppSelector(activeImageOrDuplicateOfSelector);
  const enabledImages = useAppSelector(enabledImagesSelector);
  const spacePositions = useAppSelector(spacePositionsSelector);
  const labelPositions = useAppSelector(labelPositionsSelector);
  const preferences = useAppSelector(preferencesSelector);
  const getEffectiveStyle = useAppSelector(effectiveLabelStyleSelector);

  const initialName = overlayLabels.find((label) => label.id === overlay.labelId)?.name ?? '';
  const initialLinkMode: LinkMode = overlay.spaceId ? 'existing' : (overlay.layerId ? 'layer' : 'none');
  const [name, setName] = useState(initialName);
  const [linkMode, setLinkMode] = useState<LinkMode>(initialLinkMode);
  const [selectedSpaceId, setSelectedSpaceId] = useState(overlay.spaceId ?? '');
  const [selectedLayerId, setSelectedLayerId] = useState(overlay.layerId ? String(overlay.layerId) : '');
  const [style, setStyle] = useState<OverlayStyle>(overlay.style ?? {});
  // Attributes changed in this form, applied to the other selected overlays too
  const [styleChanges, setStyleChanges] = useState<OverlayStyle>({});
  const isMultiple = otherOverlays.length > 0;
  // Rotation of the label badge, stored in its label position
  const defaultRotation = preferences.defaultLabelRotation ?? 0;
  const initialRotation = Number(
    (overlay.labelId ? labelPositions[overlay.labelId]?.rotation : undefined) ?? defaultRotation,
  );
  const [rotation, setRotation] = useState(initialRotation);

  // Label style over the map default one
  const effectiveStyle = { ...getEffectiveStyle({ id: overlay.id, name: '' }), ...style };
  const styleState = resolveLabelEditorStyleState(effectiveStyle);
  const isDirty = name !== initialName
    || linkMode !== initialLinkMode
    || selectedSpaceId !== (overlay.spaceId ?? '')
    || selectedLayerId !== (overlay.layerId ? String(overlay.layerId) : '')
    || rotation !== initialRotation
    || JSON.stringify(style) !== JSON.stringify(overlay.style ?? {});
  const canSave = (linkMode !== 'existing' || Boolean(selectedSpaceId))
    && (linkMode !== 'layer' || Boolean(selectedLayerId));
  // Layers the overlay can open: the other enabled ones
  const linkableLayers = enabledImages.filter((image) => image.id !== activeImage?.id && image.id !== ownerImage?.id);
  // Shown as label when left blank
  const linkName = linkMode === 'existing'
    ? spaces.find((space) => space.id === selectedSpaceId)?.name
    : (linkMode === 'layer' ? linkableLayers.find((layer) => String(layer.id) === selectedLayerId)?.name : undefined);
  // Spaces not shown by another overlay of this layer
  const availableSpaces = spaces.filter((space) => !overlays.some(
    (other) => other.id !== overlay.id && other.spaceId === space.id,
  ));

  const preview = (changes: Partial<Omit<OverlayStylePreview, 'id'>>, nextStyleChanges = styleChanges) => {
    const nextRotation = changes.rotation ?? rotation;
    onPreviewChange({
      id: overlay.id,
      style,
      rotation,
      name,
      ...(isMultiple && {
        others: {
          ids: otherOverlays.map((other) => other.id),
          changes: nextStyleChanges,
          rotation: nextRotation === initialRotation ? undefined : nextRotation,
        },
      }),
      ...changes,
    });
  };

  const changeStyle = (changes: OverlayStyle, nextRotation = rotation) => {
    const next = { ...style, ...changes };
    const nextStyleChanges = { ...styleChanges, ...changes };
    setStyle(next);
    setStyleChanges(nextStyleChanges);
    preview({ style: next, rotation: nextRotation }, nextStyleChanges);
  };

  const changeName = (nextName: string) => {
    setName(nextName);
    preview({ name: nextName });
  };

  const changeAttribute = (attribute: keyof LabelStyleWithRotation, value: string | number) => {
    if (attribute === 'rotation') {
      setRotation(Number(value));
      changeStyle({}, Number(value));
    } else {
      // Range and number fields give strings
      changeStyle({ [attribute]: NUMERIC_STYLE_ATTRIBUTES.includes(attribute) ? Number(value) : value });
    }
  };

  const close = useCallback(() => {
    onPreviewChange(null);
    onClose();
  }, [onClose, onPreviewChange]);

  const save = useCallback(() => {
    if (!canSave) return;

    const centroid = polygonCentroid(overlay.points);
    const labelName = labelText(name) ? labelHtml(name) : '';
    // Left blank, the label shows the space or layer name, so it's kept to place it
    const hasLabel = Boolean(labelName) || linkMode !== 'none';
    groupedUndo.batch(() => {
      // Label: created, renamed or removed from its name
      let labelId = overlay.labelId;
      const labelRotation = rotation === defaultRotation ? undefined : rotation;
      if (hasLabel && labelId) {
        dispatch(updateLabel({ id: labelId, name: labelName }));
        if (rotation !== initialRotation) {
          dispatch(rotateLabelInImage({ id: labelId, rotation: labelRotation }));
        }
      } else if (hasLabel) {
        labelId = uuidv4();
        dispatch(updateLabel({ id: labelId, name: labelName }));
        const position = newLabelPosition ?? centroid;
        dispatch(addLabelInImage({ label: labelId, lat: position.x, lng: position.y, rotation: labelRotation }));
      } else if (labelId) {
        dispatch(removeLabelInImage(labelId));
        labelId = null;
      }

      // Space: placed at the overlay centroid when not placed yet. A new space
      // is linked by the space editor once created
      let spaceId = linkMode === 'new' ? overlay.spaceId : null;
      if (linkMode === 'existing') {
        spaceId = selectedSpaceId;
        if (!spacePositions[spaceId]) {
          dispatch(addSpaceInImage({ space: spaceId, lat: centroid.x, lng: centroid.y }));
        }
      }

      // Layer: opened when clicked, instead of a space
      const layerId = linkMode === 'layer' ? Number(selectedLayerId) : null;

      dispatch(updateOverlayInImage({ id: overlay.id, changes: { labelId, spaceId, layerId, style } }));

      // Other selected overlays: only the changed attributes, keeping their differences
      otherOverlays.forEach((other) => {
        if (Object.keys(styleChanges).length > 0) {
          dispatch(updateOverlayInImage({ id: other.id, changes: { style: { ...other.style, ...styleChanges } } }));
        }
        if (rotation !== initialRotation && other.labelId) {
          dispatch(rotateLabelInImage({ id: other.labelId, rotation: labelRotation }));
        }
      });
    });

    if (linkMode === 'new') {
      // Create the space in the sidebar space editor, shown as this overlay
      dispatch(setEditedSpace({
        id: 'newSpace',
        position: { lat: centroid.x, lng: centroid.y, layerId: activeImage?.id },
        overlay: { id: overlay.id, name: labelText(name) },
      }));
    }
    close();
  }, [
    activeImage?.id, canSave, close, defaultRotation, dispatch, initialRotation, name, newLabelPosition, otherOverlays,
    overlay, rotation, selectedLayerId, selectedSpaceId, linkMode, spacePositions, style, styleChanges,
  ]);

  // Deletes all selected overlays, with their labels
  const remove = () => {
    groupedUndo.batch(() => {
      [overlay, ...otherOverlays].forEach((removed) => {
        dispatch(removeOverlayInImage(removed.id));
        if (removed.labelId) dispatch(removeLabelInImage(removed.labelId));
      });
    });
    close();
  };

  // Save pending changes when clicking the global Save button
  useEffect(() => {
    const flushPendingEdits = (flushState?: { didFlush?: boolean; blocked?: boolean }) => {
      if (!isDirty) return;
      if (!canSave || linkMode === 'new') {
        // Needs a space or layer to be selected, or a space created in the space editor first
        if (flushState) flushState.blocked = true;
        if (canSave) save();
        return;
      }
      save();
      if (flushState) flushState.didFlush = true;
    };
    eventBus.on('flush-pending-edits', flushPendingEdits);
    return () => {
      eventBus.off('flush-pending-edits', flushPendingEdits);
    };
  }, [canSave, isDirty, save, linkMode]);

  return (
    <aside aria-label="Overlay editor" className="theme-sidebar label-editor-drawer overlay-editor-drawer" tabIndex={-1}>
      <header className="theme-sidebar__header">
        <div className="theme-sidebar__title">
          <FontAwesomeIcon icon={faDrawPolygon} />
          <h3>Overlay Editor</h3>
        </div>
        <button type="button" aria-label="Close overlay editor" onClick={close} className="theme-sidebar__close">
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </header>

      <div className="theme-sidebar__content label-editor-drawer__content">
        <form
          className="label-editor-form overlay-editor-form"
          onSubmit={(event) => {
            event.preventDefault();
            save();
          }}
        >
          <EditLabelToolbar onDelete={remove} deleteLabel={isMultiple ? 'Delete overlays' : 'Delete overlay'} />
          {isMultiple ? (
            <p className="overlay-editor-form__hint">
              Editing {otherOverlays.length + 1} overlays: style changes apply to all of them.
            </p>
          ) : (
            <>
              <fieldset className="overlay-editor-form__space">
                <legend>Link</legend>
                {([
                  ['none', 'No link'],
                  ['new', 'Create space'],
                  ['existing', 'Link to existing space'],
                  ['layer', 'Link to layer'],
                ] as const).map(([value, text]) => (
                  <label key={value}>
                    <input
                      type="radio"
                      name="overlay-space"
                      value={value}
                      checked={linkMode === value}
                      onChange={() => setLinkMode(value)}
                    />
                    {text}
                  </label>
                ))}
                {linkMode === 'existing' && (
                  <label className="theme-select">
                    <span className="theme-select__label">Space</span>
                    <select
                      className="theme-select__control"
                      value={selectedSpaceId}
                      onChange={(event) => setSelectedSpaceId(event.target.value)}
                      required
                    >
                      <option value="">Select space</option>
                      {availableSpaces.map((space) => (
                        <option key={space.id} value={space.id}>{space.name}</option>
                      ))}
                    </select>
                  </label>
                )}
                {linkMode === 'layer' && (
                  <label className="theme-select">
                    <span className="theme-select__label">Layer</span>
                    <select
                      className="theme-select__control"
                      value={selectedLayerId}
                      onChange={(event) => setSelectedLayerId(event.target.value)}
                      required
                    >
                      <option value="">Select layer</option>
                      {linkableLayers.map((layer) => (
                        <option key={layer.id} value={layer.id}>{layer.name}</option>
                      ))}
                    </select>
                  </label>
                )}
              </fieldset>

              <div className="theme-select">
                <span className="theme-select__label">Label</span>
                <LabelNameEditor
                  aria-label="Label"
                  value={name}
                  placeholder={linkName ?? 'No label'}
                  onChange={changeName}
                />
              </div>
            </>
          )}

          <label className="theme-toggle">
            <span className="theme-toggle__label">Always show label</span>
            <input
              type="checkbox"
              checked={Boolean(style.alwaysShowLabel)}
              onChange={(event) => changeStyle({ alwaysShowLabel: event.target.checked || undefined })}
            />
          </label>
          <EditLabelStyleFields
            variant={styleState.variant}
            fontSize={styleState.fontSize}
            fontWeight={styleState.fontWeight}
            color={styleState.color}
            backgroundColor={styleState.backgroundColor}
            backgroundOpacity={styleState.backgroundOpacity}
            borderColor={styleState.borderColor}
            padding={styleState.padding}
            rotation={rotation}
            skewX={styleState.skewX}
            skewY={styleState.skewY}
            perspectiveX={styleState.perspectiveX}
            perspectiveY={styleState.perspectiveY}
            onAttributeChange={changeAttribute}
          />
          <label className="theme-toggle">
            <span className="theme-toggle__label">Scale with map zoom</span>
            <input
              type="checkbox"
              checked={style.scaleZoom !== undefined}
              onChange={(event) => changeStyle({ scaleZoom: event.target.checked ? map.getZoom() : undefined })}
            />
          </label>
          {style.scaleZoom !== undefined && (
            <p className="overlay-editor-form__hint">
              The label keeps the size shown at the zoom it was enabled on, relative to the map.
            </p>
          )}
          {!isMultiple && (
            <p className="overlay-editor-form__hint">
              Drag points to reshape, click a midpoint to add a point,
              right-click (or Alt+click) a point to remove it.
            </p>
          )}
        </form>
      </div>

      <footer className="theme-sidebar__footer label-editor-drawer__footer">
        <button
          type="button"
          className="label-editor-drawer__button save"
          onClick={() => save()}
          disabled={!canSave}
        >
          Save
        </button>
      </footer>
    </aside>
  );
}
